/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { usePress } from '../hooks/UNSAFE_usePress';

type Props = {
  /**
   * Sets the URL that the hyperlink points to.
   */
  href: string;

  /**
   * Sets the variant.
   */
  variant?: 'default' | 'primary' | 'secondary';

  /**
   * Sets the placement.
   */
  placement?: 'standalone' | 'embedded';

  /**
   * Sets the target.
   */
  target?: string;

  /**
   * One or more ids (separated by spaces) of elements that label the Link.
   */
  ariaLabelledBy?: string;

  /**
   * Triggered when a link is clicked, whether by keyboard, mouse, or touch events.
   */
  onClick?: (event: Event) => void;

  /**
   * Specifies the children
   */
  children?: ComponentChildren;
};

const styles = {
  default: cssProps`${{
    display: 'inline',
    color: 'var(--oj-c-link-text-color)',
    textDecorationColor: 'var(--oj-c-link-text-color)',
    textDecorationThickness: '1px',
    textUnderlineOffset: 'var(--oj-c-link-text-underline-offset)',
    cursor: 'var( --oj-c-PRIVATE-DO-NOT-USE-core-cursor-clickable)',
    '&:visited': { color: '--oj-c-link-text-color-visited' },
    '&:active': { color: '--oj-c-link-text-color-active' },
    '&:focus-visible': {
      outlineColor: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-focus-border-color)',
      outlineStyle: 'dotted',
      outlineWidth: '1px',
      outlineOffset: '1px'
    }
  }}`,
  primary: cssProps`${{
    '--oj-c-link-text-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)',
    '--oj-c-link-text-color-visited': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)',
    '--oj-c-link-text-color-active': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-primary)'
  }}`,
  secondary: cssProps`${{
    '--oj-c-link-text-color': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-secondary)',
    '--oj-c-link-text-color-visited': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-secondary)',
    '--oj-c-link-text-color-active': 'var(--oj-c-PRIVATE-DO-NOT-USE-core-text-color-secondary)'
  }}`,
  standalone: cssProps`${{
    textDecorationLine: 'var(--oj-c-link-standalone-text-decoration)',
    '&:hover': { textDecorationLine: 'var(--oj-c-link-standalone-text-decoration-hover)' },
    '&:active': { textDecorationLine: 'var(--oj-c-link-standalone-text-decoration-hover)' }
  }}`,
  embedded: cssProps`${{
    textDecorationLine: 'var(--oj-c-link-embedded-text-decoration)',
    '&:hover': { textDecorationLine: 'var(--oj-c-link-embedded-text-decoration-hover)' },
    '&:active': { textDecorationLine: 'var(--oj-c-link-embedded-text-decoration-hover)' }
  }}`
};

const noop = () => {};

export function Link({
  href,
  variant = 'default',
  placement = 'standalone',
  target,
  ariaLabelledBy,
  children,
  onClick
}: Props) {
  const linkClass = classNames([
    styles.default,
    styles[placement],
    variant !== 'default' && styles[variant]
  ]);
  const { pressProps } = usePress(onClick ?? noop);
  return (
    <a
      href={href}
      aria-labelledby={ariaLabelledBy}
      target={target}
      {...pressProps}
      class={linkClass}>
      {children}
    </a>
  );
}
